/**
 * "Add ESC/POS printer" on a desktop register.
 *
 * Two ways in — **Scan network** (a bounded, cancellable search of one chosen
 * network on an editable port) or **Add by IP address** — and one way through:
 * check → test page → save, or save and use. A candidate is never saved by
 * scanning or testing it; leaving without saving releases its connection.
 * Mobile never renders this component (`printer-list-dialog.tsx` routes on
 * the printing capability and the chosen method).
 */
import { useMemo, useEffect } from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import MuiPrintIcon from '@mui/icons-material/Print';
import ToggleButton from '@mui/material/ToggleButton';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { usePrinterStore } from 'src/store/PrinterStore';
import { getPlatformPrinter } from 'src/platform/services';
import type { SavedEscPosPrinter } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

import EscPosAttachedPanel from './escpos-attached-panel';
import EscPosDiscoveryPanel from './escpos-discovery-panel';
import EscPosCandidatePanel from './escpos-candidate-panel';
import { type EscPosSetupMode, useEscPosPrinterSetup } from './use-escpos-printer-setup';

// ----------------------------------------------------------------------

/** Attached printers (Windows queues, COM ports) exist only where the shell has an OS print bridge. */
const attachedAvailable = getPlatformPrinter().escPosAttachedPrinting;

type ConnectionKind = 'network' | 'attached';

export interface EscPosPrinterDialogProps {
  open: boolean;
  onClose: () => void;
  /** `use` is true for "Save and use"; false remembers the printer without activating it. */
  onSaved: (printer: SavedEscPosPrinter, use: boolean) => void;
  currentDefaultId?: string | null;
}

export default function EscPosPrinterDialog({ open, onClose, onSaved, currentDefaultId }: EscPosPrinterDialogProps) {
  const remembered = usePrinterStore((state) => state.rememberedPrinters);
  const rememberedIds = useMemo(() => new Set(remembered.map((p) => p.identifier)), [remembered]);
  const setup = useEscPosPrinterSetup((printer, use) => {
    onSaved(printer, use);
    onClose();
  });
  const { mode, scan, candidate, changeMode, discardCandidate, cancelScan } = setup;
  const kind: ConnectionKind = mode === 'attached' ? 'attached' : 'network';

  // Start on the attached list where the register has one (the USB printer is
  // the common case), otherwise on the scan tab. The operator presses Scan
  // themselves, so nothing probes the network merely because a dialog opened.
  useEffect(() => {
    if (open) changeMode(attachedAvailable ? 'attached' : 'scan');
  }, [open, changeMode]);

  const changeKind = (_event: unknown, next: ConnectionKind | null) => {
    if (next === null) return;
    changeMode(next === 'attached' ? 'attached' : 'scan');
  };

  const handleClose = () => {
    cancelScan();
    discardCandidate();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <MuiIcon icon={MuiPrintIcon} />
          </Avatar>
          <Box>
            <Typography variant="h6">{candidate ? 'Check printer' : 'Add ESC/POS printer'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {candidate
                ? 'Make sure it answers and prints before saving it'
                : 'Generic ESC/POS receipt printers: on this shop’s network, or attached to this register'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {candidate ? (
          <EscPosCandidatePanel
            candidate={candidate}
            name={setup.name}
            presetId={setup.presetId}
            saving={setup.saving}
            baudRate={setup.baudRate}
            onBaudRateChange={setup.setBaudRate}
            onNameChange={setup.setName}
            onPresetChange={setup.choosePreset}
            onTestPrint={setup.testPrint}
            onSave={() => setup.save(false)}
            onSaveAndUse={() => setup.save(true)}
            onBack={discardCandidate}
          />
        ) : (
          <>
            {attachedAvailable && (
              <Box sx={{ px: 3, pb: 2 }}>
                <ToggleButtonGroup exclusive fullWidth size="small" color="primary" value={kind} onChange={changeKind}>
                  <ToggleButton value="network">Network</ToggleButton>
                  <ToggleButton value="attached">Attached printer</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {kind === 'attached' ? (
              <EscPosAttachedPanel
                devices={setup.devices}
                savedIds={rememberedIds}
                currentDefaultId={currentDefaultId}
                onRefresh={setup.loadDevices}
                onSelectQueue={setup.selectQueue}
                onSelectSerialPort={setup.selectSerialPort}
              />
            ) : (
              <Tabs
                  value={mode}
                  onChange={(_event, next: EscPosSetupMode) => changeMode(next)}
                  variant="fullWidth"
                  sx={{ px: 3, mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab value="scan" label="Scan network" />
                  <Tab value="manual" label="Add by IP address" />
                </Tabs>
            )}

            {kind === 'network' && mode === 'scan' && (
              <EscPosDiscoveryPanel
                scan={scan}
                port={setup.port}
                savedIds={rememberedIds}
                currentDefaultId={currentDefaultId}
                onPortChange={setup.setPort}
                onSearch={setup.discover}
                onCancel={cancelScan}
                onChooseInterface={setup.chooseInterface}
                onSelect={setup.selectCandidate}
                onAddByAddress={() => changeMode('manual')}
              />
            )}
            {kind === 'network' && mode === 'manual' && (
              <Stack
                component="form"
                spacing={2}
                sx={{ px: 3, pb: 2 }}
                onSubmit={(event) => {
                  event.preventDefault();
                  setup.checkAddress();
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Use this when the printer is on another network, or the scan did not find it. The IP
                  address is on the printer’s self-test page or in its network settings.
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Printer IP address"
                    placeholder="192.168.1.50"
                    value={setup.addressInput}
                    onChange={(event) => setup.setAddressInput(event.target.value)}
                    error={setup.addressError !== null}
                    helperText={setup.addressError ?? 'Give the printer a DHCP reservation or a static IP so it keeps this address.'}
                    inputProps={{ inputMode: 'decimal', autoComplete: 'off', spellCheck: false }}
                  />
                  <TextField
                    label="Port"
                    value={setup.port}
                    onChange={(event) => setup.setPort(event.target.value)}
                    inputProps={{ inputMode: 'numeric', style: { width: 72 } }}
                  />
                </Stack>
                <Box>
                  <Button type="submit" variant="contained" disabled={setup.addressInput.trim().length === 0}>
                    Check printer
                  </Button>
                </Box>
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      {!candidate && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
